/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { Company, UserPermission } from 'api';
import {
  FIRM_COMPANY_USER_PERMISSIONS_QUERY_KEY,
  FIRM_COMPANY_USER_PERMISSIONS_STALE_TIME,
} from 'common/constants/services/firms';
import { COMPANY, USE_PORTAL } from 'common/constants/user';
import { FirmUserWithPermissions } from 'services/hooks/useCompanyPermissions.types';
import { getFirmUserPermissions } from 'services/hooks/useUserSelected';
import { Logger } from 'utilities/logger';
import { ExtendedCompanyDataWithPermissions } from './types';

const EMPTY_CATEGORIES = {
  companiesWithMeasurementDateAndUsers: [] as ExtendedCompanyDataWithPermissions[],
  companiesWithMeasurementDateButNoUsers: [] as ExtendedCompanyDataWithPermissions[],
  companiesWithoutMeasurementDateButUsers: [] as ExtendedCompanyDataWithPermissions[],
  companiesWithoutMeasurementDateAndUsers: [] as ExtendedCompanyDataWithPermissions[],
  companiesWithRequests: [] as ExtendedCompanyDataWithPermissions[],
};

const isPortalPermissionForCompany = (permission: UserPermission, companyId: number) => {
  const { feature_object: featureObject, access_type: accessType } = permission;
  if (!featureObject) {
    return false;
  }

  return (
    featureObject.object_type === COMPANY &&
    accessType === USE_PORTAL &&
    Number(featureObject.object_id) === companyId
  );
};

const getCompanyContacts = (
  companyId: number,
  firmCompanyUsers: FirmUserWithPermissions[]
): { id: number; email: string }[] => {
  const contactsById = new Map<number, { id: number; email: string }>();

  firmCompanyUsers.forEach(user => {
    if (user.id === undefined) {
      return;
    }

    const hasPortalAccess = (user.user_permission_set ?? []).some(permission =>
      isPortalPermissionForCompany(permission, companyId)
    );
    if (!hasPortalAccess) {
      return;
    }

    contactsById.set(user.id, {
      id: user.id,
      email: user.email || '',
    });
  });

  return Array.from(contactsById.values());
};

const useCompanyCategoriesProcessManagement = ({ firmId, companies }: { firmId: number; companies: Company[] }) => {
  const HAS_MEASUREMENT_DATE = true;
  const NO_MEASUREMENT_DATE = false;
  const HAS_USERS = true;
  const NO_USERS = false;

  const [companyCategories, setCompanyCategories] = useState(EMPTY_CATEGORIES);

  const {
    data: firmCompanyUsers = [],
    error,
    isError,
  } = useQuery({
    enabled: Boolean(firmId) && Boolean(companies?.length),
    queryKey: [FIRM_COMPANY_USER_PERMISSIONS_QUERY_KEY, firmId],
    queryFn: () => getFirmUserPermissions(firmId) as Promise<FirmUserWithPermissions[]>,
    retry: false,
    staleTime: FIRM_COMPANY_USER_PERMISSIONS_STALE_TIME,
  });

  useEffect(() => {
    if (!isError) {
      return;
    }

    enqueueSnackbar(
      'Unable to load company contacts for this firm. Companies may appear without contacts.',
      { variant: 'error' }
    );
    Logger.error(`Error fetching firm company user permissions for firm ID ${firmId}`, error);
  }, [error, firmId, isError]);

  const processCompany = useCallback(
    (company: Company, companyUsers: FirmUserWithPermissions[]): ExtendedCompanyDataWithPermissions => {
      const companyId = Number(company.id);
      const usersWithCompanyPermissions =
        Number.isFinite(companyId) && !isError ? getCompanyContacts(companyId, companyUsers) : [];

      const baseCompany: ExtendedCompanyDataWithPermissions = {
        ...company,
        requested_documents: (company as any).requested_documents || [],
        questions: (company as any).questions || [],
        has_tasks: (company as any).has_tasks,
        latest_cmd: (company as any).latest_cmd,
      };

      if (usersWithCompanyPermissions.length > 0) {
        return { ...baseCompany, users_with_company_permissions: usersWithCompanyPermissions };
      }
      return baseCompany;
    },
    [isError]
  );

  // Contacts enrich companies; contact-fetch failure still leaves companies visible.
  const [companiesWithUsers, companiesWithoutUsers, companiesWithRequests] = useMemo(() => {
    if (!companies?.length) {
      return [[], [], []] as [
        ExtendedCompanyDataWithPermissions[],
        ExtendedCompanyDataWithPermissions[],
        ExtendedCompanyDataWithPermissions[]
      ];
    }

    const withUsers: ExtendedCompanyDataWithPermissions[] = [];
    const withoutUsers: ExtendedCompanyDataWithPermissions[] = [];
    const withRequests: ExtendedCompanyDataWithPermissions[] = [];

    companies.forEach(company => {
      const processedCompany = processCompany(company, firmCompanyUsers);
      if (processedCompany.has_tasks) {
        withRequests.push(processedCompany);
      } else if (processedCompany.users_with_company_permissions) {
        withUsers.push(processedCompany);
      } else {
        withoutUsers.push(processedCompany);
      }
    });

    return [withUsers, withoutUsers, withRequests];
  }, [companies, firmCompanyUsers, processCompany]);

  const companyHasUsersWithPermissions = (company: ExtendedCompanyDataWithPermissions): boolean =>
    Boolean(company.users_with_company_permissions);
  const companyHasMeasurementDate = (company: ExtendedCompanyDataWithPermissions): boolean =>
    Boolean(company.latest_cmd);

  const filterCompanies = (
    companiesParam: ExtendedCompanyDataWithPermissions[],
    hasMeasurementDateCondition: boolean,
    hasUsersCondition: boolean
  ): ExtendedCompanyDataWithPermissions[] =>
    companiesParam.filter(
      company =>
        companyHasMeasurementDate(company) === hasMeasurementDateCondition &&
        companyHasUsersWithPermissions(company) === hasUsersCondition
    );

  const handleSetCompaniesCategories = useCallback((): void => {
    setCompanyCategories({
      companiesWithMeasurementDateAndUsers: filterCompanies(companiesWithUsers, HAS_MEASUREMENT_DATE, HAS_USERS),
      companiesWithMeasurementDateButNoUsers: filterCompanies(
        companiesWithoutUsers,
        HAS_MEASUREMENT_DATE,
        NO_USERS
      ),
      companiesWithoutMeasurementDateButUsers: filterCompanies(companiesWithUsers, NO_MEASUREMENT_DATE, HAS_USERS),
      companiesWithoutMeasurementDateAndUsers: filterCompanies(
        companiesWithoutUsers,
        NO_MEASUREMENT_DATE,
        NO_USERS
      ),
      companiesWithRequests,
    });
  }, [companiesWithoutUsers, companiesWithUsers, companiesWithRequests]);

  useEffect(() => {
    if (companiesWithUsers.length || companiesWithoutUsers.length || companiesWithRequests.length) {
      handleSetCompaniesCategories();
      return;
    }

    setCompanyCategories(EMPTY_CATEGORIES);
  }, [companiesWithUsers, companiesWithoutUsers, companiesWithRequests, handleSetCompaniesCategories]);

  return { companyCategories };
};

export default useCompanyCategoriesProcessManagement;
